/**
 * Privacy & Data Protection Utilities
 * 
 * GDPR-compliant data masking, encryption helpers, and privacy functions.
 * Used throughout the application to protect sensitive personal data.
 */

/**
 * Masks an email address for display
 * Example: john.doe@example.com → jo****@example.com
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '';
  
  const [localPart, domain] = email.split('@');
  if (!domain) return email; // Invalid email
  
  if (localPart.length <= 2) {
    return `${localPart[0]}*@${domain}`;
  }
  
  const visibleChars = Math.min(2, Math.floor(localPart.length * 0.3));
  const masked = localPart.substring(0, visibleChars) + '****';
  
  return `${masked}@${domain}`;
}

/**
 * Masks a phone number for display
 * Example: +4512345678 → +45****5678
 * Example: 12345678 → 12****78
 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  if (cleaned.length < 4) {
    return cleaned[0] + '***';
  }
  
  if (cleaned.startsWith('+')) {
    // International format
    const countryCode = cleaned.substring(0, 3); // e.g., +45
    const rest = cleaned.substring(3);
    
    if (rest.length <= 4) {
      return countryCode + '****';
    }
    
    const lastDigits = rest.slice(-4);
    return `${countryCode}****${lastDigits}`;
  } else {
    // Domestic format
    const firstTwo = cleaned.substring(0, 2);
    const lastTwo = cleaned.slice(-2);
    return `${firstTwo}****${lastTwo}`;
  }
}

/**
 * Masks a CPR number (Danish social security number)
 * ONLY the last 4 digits should ever be stored. This is for display.
 * Example: 1234 → ******1234
 */
export function maskCPR(cprLast4: string | null | undefined): string {
  if (!cprLast4) return '';
  
  // CPR format: DDMMYY-XXXX (10 digits)
  // We only store last 4, so display as: ******1234
  return `******${cprLast4}`;
}

/**
 * Masks a name for privacy
 * Example: John Michael Doe → John M. D.
 */
export function maskName(fullName: string | null | undefined, showFirst: boolean = true): string {
  if (!fullName) return '';
  
  const parts = fullName.trim().split(/\s+/);
  
  if (parts.length === 1) {
    return showFirst ? parts[0] : parts[0][0] + '.';
  }
  
  if (showFirst) {
    const firstName = parts[0];
    const initials = parts.slice(1).map(p => p[0] + '.').join(' ');
    return `${firstName} ${initials}`;
  } else {
    return parts.map(p => p[0] + '.').join(' ');
  }
}

/**
 * Masks an address for privacy
 * Example: Hovedgaden 123, 2. th → Hovedgaden ***, 2. th
 */
export function maskAddress(address: string | null | undefined): string {
  if (!address) return '';
  
  // Replace street numbers with ***
  return address.replace(/\d+/g, '***');
}

/**
 * Redacts sensitive data from objects (for logging, error reporting)
 * Removes or masks fields like email, phone, cpr, password, etc.
 */
export function redactSensitiveData<T extends Record<string, any>>(
  data: T,
  options: {
    maskFields?: string[];
    removeFields?: string[];
  } = {}
): Partial<T> {
  const {
    maskFields = ['email', 'phone', 'telefon', 'mobile'],
    removeFields = ['password', 'token', 'secret', 'cpr', 'ssn', 'auth', 'apiKey']
  } = options;
  
  const result: any = { ...data };
  
  for (const key in result) {
    const lowerKey = key.toLowerCase();
    
    // Remove sensitive fields entirely
    if (removeFields.some(field => lowerKey.includes(field.toLowerCase()))) {
      delete result[key];
      continue;
    }
    
    // Mask display fields
    if (maskFields.some(field => lowerKey.includes(field.toLowerCase()))) {
      const value = result[key];
      
      if (typeof value === 'string') {
        if (lowerKey.includes('email')) {
          result[key] = maskEmail(value);
        } else if (lowerKey.includes('phone') || lowerKey.includes('telefon')) {
          result[key] = maskPhone(value);
        } else {
          result[key] = '***REDACTED***';
        }
      }
    }
    
    // Recursively redact nested objects
    if (result[key] && typeof result[key] === 'object' && !Array.isArray(result[key])) {
      result[key] = redactSensitiveData(result[key], options);
    }
    
    // Handle arrays
    if (Array.isArray(result[key])) {
      result[key] = result[key].map((item: any) =>
        typeof item === 'object' ? redactSensitiveData(item, options) : item
      );
    }
  }
  
  return result;
}

/**
 * Validates that a string doesn't contain CPR numbers (Danish SSN)
 * CPR format: DDMMYY-XXXX or DDMMYYXXXX
 * Returns true if CPR pattern detected (should not be stored!)
 */
export function containsCPR(text: string | null | undefined): boolean {
  if (!text) return false;
  
  // Pattern: 6 digits (date) + optional dash + 4 digits
  const cprPattern = /\b\d{6}-?\d{4}\b/;
  
  return cprPattern.test(text);
}

/**
 * Sanitizes CPR input at the write boundary.
 * Whatever the user typed (full CPR, last 4, already-masked), only the
 * last 4 digits survive — full CPR must never be persisted.
 * Example: 010180-1234 → ******1234, 1234 → ******1234
 */
export function sanitizeCPRInput(value: string | null | undefined): string {
  if (!value) return '';

  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';

  return maskCPR(digits.slice(-4));
}

/**
 * Display-safe CPR: masks legacy stored values that still contain a full
 * CPR; already-masked or last-4-only values pass through untouched.
 */
export function displayCPR(value: string | null | undefined): string {
  if (!value) return '';

  return containsCPR(value) ? sanitizeCPRInput(value) : value;
}

/**
 * Strips potential CPR numbers from text
 */
export function stripCPR(text: string | null | undefined): string {
  if (!text) return '';
  
  // Replace CPR patterns with placeholder
  return text.replace(/\b\d{6}-?\d{4}\b/g, '[CPR FJERNET]');
}

/**
 * Formats file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Checks if a file type is allowed (for document uploads)
 */
export function isAllowedFileType(mimeType: string): boolean {
  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  
  return allowedTypes.includes(mimeType);
}

/**
 * Gets document type display name in Danish
 */
export function getDocumentTypeName(docType: string): string {
  const typeNames: Record<string, string> = {
    straffeattest: 'Straffeattest',
    uddannelsesbevis: 'Uddannelsesbevis',
    cv: 'CV',
    id_dokument: 'ID-dokument',
    samarbejdsaftale: 'Samarbejdsaftale',
    dpa: 'Databehandleraftale',
    gdpr_dokumentation: 'GDPR-dokumentation',
    andet: 'Andet dokument',
  };
  
  return typeNames[docType] || docType;
}

/**
 * Calculates document expiry date based on type
 */
export function calculateDocumentExpiry(docType: string): Date {
  const expiryPeriods: Record<string, number> = {
    straffeattest: 365, // 1 year
    uddannelsesbevis: 3650, // 10 years (permanent education)
    cv: 730, // 2 years
    id_dokument: 1825, // 5 years (typical ID validity)
    samarbejdsaftale: 2555, // 7 years (legal requirement)
    dpa: 2555, // 7 years
    gdpr_dokumentation: 2555, // 7 years
    andet: 730, // 2 years default
  };
  
  const days = expiryPeriods[docType] || 730;
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + days);
  
  return expiryDate;
}

/**
 * Checks if a document is expiring soon (within 30 days)
 */
export function isDocumentExpiringSoon(expiresAt: string | Date | null | undefined): boolean {
  if (!expiresAt) return false;
  
  const expiry = new Date(expiresAt);
  const now = new Date();
  const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
}

/**
 * Checks if a document is expired
 */
export function isDocumentExpired(expiresAt: string | Date | null | undefined): boolean {
  if (!expiresAt) return false;
  
  const expiry = new Date(expiresAt);
  const now = new Date();
  
  return expiry < now;
}

/**
 * Gets document status based on expiry
 */
export function getDocumentStatus(expiresAt: string | Date | null | undefined): {
  status: 'active' | 'expiring_soon' | 'expired';
  label: string;
  variant: 'default' | 'warning' | 'error';
} {
  if (isDocumentExpired(expiresAt)) {
    return {
      status: 'expired',
      label: 'Udløbet',
      variant: 'error',
    };
  }
  
  if (isDocumentExpiringSoon(expiresAt)) {
    return {
      status: 'expiring_soon',
      label: 'Udløber snart',
      variant: 'warning',
    };
  }
  
  return {
    status: 'active',
    label: 'Aktiv',
    variant: 'default',
  };
}

/**
 * Generates a secure random string (for backup codes, tokens, etc.)
 */
export function generateSecureCode(length: number = 8): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Exclude ambiguous chars
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  
  return Array.from(array)
    .map(x => chars[x % chars.length])
    .join('');
}

/**
 * Formats a date in Danish locale
 */
export function formatDateDK(date: string | Date | null | undefined): string {
  if (!date) return '-';
  
  return new Date(date).toLocaleDateString('da-DK', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Formats a date with time in Danish locale
 */
export function formatDateTimeDK(date: string | Date | null | undefined): string {
  if (!date) return '-';
  
  return new Date(date).toLocaleString('da-DK', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Gets relative time in Danish (e.g., "2 timer siden", "i går")
 */
export function getRelativeTimeDK(date: string | Date | null | undefined): string {
  if (!date) return '-';
  
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'lige nu';
  if (diffMins < 60) return `${diffMins} minut${diffMins !== 1 ? 'ter' : ''} siden`;
  if (diffHours < 24) return `${diffHours} time${diffHours !== 1 ? 'r' : ''} siden`;
  if (diffDays === 1) return 'i går';
  if (diffDays < 7) return `${diffDays} dage siden`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} uge${Math.floor(diffDays / 7) !== 1 ? 'r' : ''} siden`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} måned${Math.floor(diffDays / 30) !== 1 ? 'er' : ''} siden`;
  
  return `${Math.floor(diffDays / 365)} år siden`;
}

/**
 * Type guard for non-null values
 */
export function isNotNull<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Safe JSON stringify with circular reference handling
 */
export function safeStringify(obj: any, indent: number = 0): string {
  const seen = new WeakSet();
  
  return JSON.stringify(
    obj,
    (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular]';
        }
        seen.add(value);
      }
      return value;
    },
    indent
  );
}







